import { FC, memo, useEffect, useRef, useState } from 'react'
import styled, { css } from 'styled-components'
import { PhoneInput } from 'react-international-phone'
import 'react-international-phone/style.css'

type LabelPosition = 'left' | 'right' | 'top' | 'bottom'

interface InputProps {
  caption?: string;
  rightAlignCaption?: string;
  className?: string;
  error?: string;
  leftIcon?: JSX.Element;
  rightIcon?: JSX.Element;
  label?: string;
  labelPosition?: LabelPosition;
  placeholder?: string;
  required?: boolean;
  type?: 'text' | 'email' | 'number' | 'password' | 'tel' | 'url';
  value?: string | number;
  readonly?: boolean;
  disabled?: boolean;
  withBottomOffset?: boolean;
  name?: string;
  size?: number;
  onClick?: () => void;
  onChange?: (inputValue: string, inputName?: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  onRightIconClick?: () => void;
  onSubmit?: (inputValue: string) => void;
  backgroundColor?: string
  containerMargin?: string;
  borderRadius?: string;
  borderColor?: string;
  leftIconSize?: number;
  marginBottom?: string;
}

const Input: FC<InputProps> = ({
  caption = '',
  rightAlignCaption = '',
  className,
  error,
  leftIcon,
  rightIcon,
  label,
  labelPosition = 'top',
  required,
  type = 'text',
  placeholder,
  value = '',
  name,
  size = 1,
  readonly = false,
  disabled = false,
  withBottomOffset,
  onClick,
  onChange,
  onFocus,
  onBlur,
  onRightIconClick,
  onSubmit,
  backgroundColor,
  containerMargin,
  borderRadius,
  borderColor,
  leftIconSize,
  marginBottom
}) => {
  const [ cursor, setCursor ] = useState(null)
  const ref = useRef(null)

  useEffect(() => {
    const input = ref?.current
    if (input && type === 'text') input.setSelectionRange(cursor, cursor)
  }, [ ref, cursor, value, type, name ])

  const inputClasses = [
    error ? 'input-error' : '',
    rightIcon ? 'input-with-icon-right' : '',
    leftIcon ? 'input-with-icon-left' : '',
  ].join(' ')

  const labelClasses: Record<LabelPosition, string> = {
    top: 'input-label-top',
    right: 'input-label-right',
    left: 'input-label-left',
    bottom: 'input-label-bottom'
  }

  const onInputChange = (event) => {
    const inputValue = event?.target?.value || ''
    setCursor(event?.target?.selectionStart)
    onChange?.(type === 'number' && parseFloat(inputValue) < 0? 0: inputValue, name)
  }

  const onInputSubmit = (event) => {
    if(event.key !== 'Enter') return null
    const inputValue = event?.target?.value || ''
    onSubmit?.(inputValue)
  }

  const onPhoneChange = (phoneValue: string) => {
    onChange(phoneValue || '', name)
  }

  return (
    <Container
      className={className + ' main-input-container'}
      $withBottom={withBottomOffset && !error && !caption && !rightAlignCaption}
      containerMargin={containerMargin}
      marginBottom={marginBottom}
    >
      {
        label ? (
          <LabelRow>
            <StyledLabel className={labelClasses[labelPosition]} required={required}>
              <span className="center">{label}</span>
            </StyledLabel>
            {rightAlignCaption && (
              <RightAlignCaption>{rightAlignCaption}</RightAlignCaption>
            )}
          </LabelRow>
        ) : null
      }

      <ContainerInput>
        {type === 'tel' ? (
          <StyledPhoneInput
            value={value as string}
            onChange={onPhoneChange}
            disabled={disabled}
            className={error ? 'phone-input-error' : ''}
            placeholder={placeholder}
          />
        ) : (
          <>
            {
              leftIcon ? (
                <ContainerIcon className="left-icon" size={leftIconSize}>
                  {leftIcon}
                </ContainerIcon>
              ) : null
            }
            <StyledInput
              ref={ref}
              className={inputClasses}
              type={type}
              placeholder={placeholder}
              value={value}
              readOnly={readonly}
              onClick={onClick}
              onChange={onInputChange}
              onFocus={onFocus}
              onBlur={onBlur}
              disabled={disabled}
              name={name}
              key={name}
              onKeyDown={onInputSubmit}
              size={size}
              backgroundColor={backgroundColor}
              borderRadius={borderRadius}
              borderColor={borderColor}
            />
            {
              rightIcon ? (
                <ContainerIcon className="right-icon" onClick={onRightIconClick} >
                  {rightIcon}
                </ContainerIcon>
              ) : null
            }
          </>
        )}
      </ContainerInput>

      {error ? (<StyledErrorText>{error}</StyledErrorText>) : null}
      {
        !error && caption ?
          (<StyledCaptionText className="input-captions">
            <span>{caption}</span>
          </StyledCaptionText>) :
          null
      }
    </Container>
  )
}

const Container = styled.div<{ $withBottom?: boolean, containerMargin: string, marginBottom: string }>`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  ${props => props.$withBottom ? 'padding-bottom: 25px' : ''}
  margin-bottom: ${props => props.marginBottom ? props.marginBottom : '0'};
`

const LabelRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-bottom: 8px;
`

const StyledLabel = styled.label<{required: boolean}>`
  color: var(--color-input-label-primary);
  line-height: var(--font-size-small);
  ${(props) => props.required && css`&:after {content: " *"; color: var(--r-color-gray-darker)}`}
  
  &.input-label-top {
    display: inline-flex;
  }
  
  .center {
    text-align: center;
    display: inline-block;
  }
`

const RightAlignCaption = styled.span`
  color: var(--color-gray-darker);
  margin-left: auto;
`

const ContainerInput = styled.div`
  display: flex;
  position: relative;
  align-items: center;
  width: 100%;
`

const StyledInput = styled.input<{backgroundColor: string, borderRadius: string, borderColor?: string}>`
  background: ${({ backgroundColor }) => backgroundColor ? backgroundColor : 'var(--color-input-background-primary)'} ;
  border: 1px solid ${({ borderColor }) => borderColor ? borderColor : 'var(--color-input-border-primary)'};
  border-radius: ${({ borderRadius }) => borderRadius ? borderRadius : '12px'};
  height: 52px;
  outline: none;
  padding: 14px;
  width: 100%;
  font-size: var(--font-size-normal);
  text-overflow: ellipsis;
  color: var(--color-input-text-primary);

  ::placeholder {
    line-height: 24px;
    color: var(--color-input-placeholder-primary);
    font-size: var(--font-size-normal);
  }
  &.input-error {
    border: 1px solid var(--color-warning);
  }
  &.input-with-icon-right {
    padding: 1rem 2.6rem 1rem 1rem;
  }
  &.input-with-icon-left {
    padding: 1rem 1rem 1rem 54px;
  }
`

const StyledPhoneInput = styled(PhoneInput)`
  width: 100%;

  .react-international-phone-input-container {
    position: relative;
  }

  .react-international-phone-input {
    background: var(--color-input-background-primary) !important;
    border: 1px solid var(--color-input-border-primary) !important;
    border-radius: 12px !important;
    height: 52px !important;
    outline: none !important;
    padding: 14px 14px 14px 90px !important;
    width: 100% !important;
    font-size: var(--font-size-normal) !important;
    color: var(--color-input-text-primary) !important;

    &::placeholder {
      line-height: 24px;
      color: var(--color-input-placeholder-primary) !important;
      font-size: var(--font-size-normal) !important;
    }
  }

  &.phone-input-error .react-international-phone-input {
    border: 1px solid var(--color-warning);
  }

  .react-international-phone-country-selector-button {
    position: absolute;
    left: 14px;
    top: 50%;
    transform: translateY(-50%);
    background: transparent;
    border: none;
    border-right: 1px solid var(--color-input-border-primary);
    padding-right: 10px;
    margin-right: 10px;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 4px;
    z-index: 1;

    &:hover {
      background: transparent;
    }
  }

  .react-international-phone-flag-emoji {
    font-size: 20px;
  }

  .react-international-phone-country-selector-dropdown {
    background: var(--color-input-background-primary);
    border: 1px solid var(--color-input-border-primary);
    border-radius: 12px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);

    overflow-y: auto;
    z-index: 10;
  }

  .react-international-phone-country-selector-dropdown__list-item {
    padding: 10px 14px;
    cursor: pointer;
    color: var(--color-input-text-primary);

    &:hover {
      background: rgba(0, 0, 0, 0.05);
    }
    &--selected {
      background: rgba(0, 0, 0, 0.1);
    }
  }
`

const ContainerIcon = styled.div<{size?: number}>`
  display: flex;
  justify-content: center;
  align-items: center;
  width: ${({ size }) => size ? size : 28}px;
  height: ${({ size }) => size ? size : 28}px;
  border-radius: 50%;
  position: absolute;

  &.left-icon {
    left: 14px;
  }
  &.right-icon {
    right: .6rem;
  }
`

const StyledErrorText = styled.span`
  color: var(--color-warning);
  height: 25px;
  padding-top: 5px;
`

const StyledCaptionText = styled.div`
  width: 100%;
  height: 25px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: var(--r-color-gray-darker);
`

export default memo(Input)